import { useEffect, useState } from "react";
import { ReceiverBackdrop, ReceiverHeader } from "./components/world/ReceiverPrimitives";
import "./receiver-world.css";

const MIC_STATE = {
  CHECKING: "checking",
  GRANTED: "granted",
  DENIED: "denied",
  UNKNOWN: "unknown",
};

const READY_VOICE = "eve";

export default function VoiceLanding({ profile, giftId, preview = false, onStartCall, onFallbackToText }) {
  const [step, setStep] = useState(0);
  const [micState, setMicState] = useState(MIC_STATE.CHECKING);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const recipientName = profile?.recipientName || profile?.name || "there";
  const introducerName = profile?.introducerName || "Someone";

  useEffect(() => {
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      setStep(3);
      return undefined;
    }
    const timers = [
      setTimeout(() => setStep(1), 300),
      setTimeout(() => setStep(2), 1800),
      setTimeout(() => setStep(3), 3200),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (!navigator.permissions) {
      setMicState(MIC_STATE.UNKNOWN);
      return undefined;
    }

    let permissionStatus;
    navigator.permissions.query({ name: "microphone" }).then((result) => {
      permissionStatus = result;
      const syncPermission = () => {
        if (result.state === "granted") setMicState(MIC_STATE.GRANTED);
        else if (result.state === "denied") setMicState(MIC_STATE.DENIED);
        else setMicState(MIC_STATE.UNKNOWN);
      };
      syncPermission();
      result.onchange = syncPermission;
    }).catch(() => setMicState(MIC_STATE.UNKNOWN));

    return () => {
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, []);

  const handleStartCall = async () => {
    setIsLoading(true);
    setErrorMessage("");
    if (preview) {
      onStartCall("preview-only", READY_VOICE);
      return;
    }
    try {
      const res = await fetch("/api/voice-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ giftId }),
      });
      const data = await res.json();
      if (!res.ok || !data.token) {
        setErrorMessage(data.error || "Unable to start voice call. Please try again.");
        setIsLoading(false);
        return;
      }
      onStartCall(data.token, READY_VOICE);
    } catch (_) {
      setErrorMessage("Connection error. Please check your internet and try again.");
      setIsLoading(false);
    }
  };

  const micDenied = micState === MIC_STATE.DENIED;

  return (
    <main className="tgc-receiver-shell tgc-receiver-shell--voice-ready" aria-labelledby="voice-ready-heading">
      <ReceiverBackdrop />
      <ReceiverHeader eyebrow="Voice call — a gift for you" />
      <div className="tgc-receiver-main">
        <section className="tgc-voice-ready">
          {step >= 1 && (
            <div className="tgc-voice-ready__greeting tgc-receiver-reveal">
              <h1 id="voice-ready-heading">Hey {recipientName}.</h1>
              <p>{introducerName} arranged a voice conversation for you — an AI that already knows a few things about you and wants to talk.</p>
            </div>
          )}

          {step >= 2 && (
            <div className="tgc-voice-disclosure tgc-receiver-reveal">
              <span className="tgc-voice-disclosure__label">You&apos;re talking to an AI</span>
              This is a real-time voice conversation with an AI — not a human, not a recording. It&apos;s been briefed on a few things about you by {introducerName}. You don&apos;t need to explain yourself from scratch.
            </div>
          )}

          {step >= 3 && (
            <div className="tgc-voice-actions tgc-receiver-reveal">
              {micDenied && (
                <div className="tgc-receiver-notice tgc-receiver-notice--error" role="alert">
                  Microphone access is blocked. Enable it in your browser settings, then refresh.
                </div>
              )}
              {errorMessage && (
                <div className="tgc-receiver-notice tgc-receiver-notice--error" role="alert">
                  {errorMessage}
                </div>
              )}
              <button
                className="tgc-voice-primary"
                type="button"
                onClick={handleStartCall}
                disabled={isLoading || micDenied}
                aria-label="Start voice call"
              >
                {isLoading ? "Connecting…" : "Start Call →"}
              </button>
              <button className="tgc-voice-secondary" type="button" onClick={onFallbackToText}>
                Prefer to type? Use text mode instead
              </button>
              <p className="tgc-voice-note">Tapping "Start Call" will ask for microphone access.</p>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
